import { useMemo } from 'react';
import { r, s } from '@/theme/tokens';
import { chartBarSizing } from '../theme/chartBarSizing';
import { Box } from './Box';
import { resolveChartPlotAreaBackgroundColor } from './chartPlotAreaBackgroundColor';
import { useTheme } from './hooks';
import { Skeleton } from './Skeleton';

type StackedBarChartSkeletonProps = {
  height?: number;
  plotAreaHeight?: number;
  bars?: number;
  showPlotAreaBackground?: boolean;
  hideYAxisLine?: boolean;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const StackedBarChartSkeleton = ({
  height = 200,
  plotAreaHeight,
  bars = 7,
  showPlotAreaBackground = true,
  hideYAxisLine = false,
}: StackedBarChartSkeletonProps): JSX.Element => {
  const categoryGapRatio = chartBarSizing.default.categoryGapRatio;
  const theme = useTheme();
  const yAxisGutter = `calc(${s('xl')} + ${s('sm')})`;
  const chartTop = s('2xl');
  const chartRight = s('xl');
  const chartBottom = `calc(${s('2xl')} + ${s('xl')})`;
  const chartInnerPaddingX = s('md');
  const barHeightBasis = plotAreaHeight ?? height;
  const resolvedContainerHeight =
    plotAreaHeight === undefined
      ? height
      : `calc(${String(plotAreaHeight)}px + ${chartTop} + ${chartBottom})`;
  const axisLeft = `calc(${yAxisGutter} + ${chartInnerPaddingX})`;
  const axisBottom = chartBottom;
  const plotAreaBackgroundColor = resolveChartPlotAreaBackgroundColor(theme);
  const barHeights = useMemo(() => {
    const resolvedBars = clamp(bars, 4, 18);
    return Array.from({ length: resolvedBars }, (_, index) => {
      const offset = Math.abs((index % 5) - 2);
      const multiplier = 0.4 + offset * 0.12;
      return clamp(barHeightBasis * multiplier, barHeightBasis * 0.35, barHeightBasis * 0.9);
    });
  }, [barHeightBasis, bars]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: resolvedContainerHeight }}>
      <Box
        sx={{
          position: 'absolute',
          left: axisLeft,
          right: chartRight,
          bottom: axisBottom,
          top: chartTop,
          backgroundColor: showPlotAreaBackground ? plotAreaBackgroundColor : 'transparent',
          display: 'flex',
          alignItems: 'flex-end',
          gap: `calc(${s('xl')} * ${categoryGapRatio / chartBarSizing.default.categoryGapRatio})`,
          paddingLeft: chartInnerPaddingX,
          paddingRight: chartInnerPaddingX,
          boxShadow: [
            `inset 1px 0 0 ${theme.app.colors.divider}`,
            `inset -1px 0 0 ${theme.app.colors.divider}`,
            `inset 0 1px 0 ${theme.app.colors.divider}`,
            `inset 0 -1px 0 ${theme.app.colors.divider}`,
          ].join(', '),
          opacity: hideYAxisLine ? 1 : 0.7,
        }}
      >
        {[0.25, 0.5, 0.75].map((offset) => (
          <Box
            key={`stacked-chart-skeleton-guide-${offset}`}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: `${offset * 100}%`,
              height: '1px',
              backgroundColor: theme.app.colors.divider,
              opacity: 0.4,
            }}
          />
        ))}
        {barHeights.map((barHeight, index) => (
          <Box
            key={`bar-${index}`}
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              gap: s('xxxs'),
              flex: 1,
              minWidth: 0,
            }}
          >
            <Skeleton
              variant="rectangular"
              height={barHeight}
              sx={{
                width: '65%',
                maxWidth: '132px',
                mx: 'auto',
                borderRadius: `${r('md')} ${r('md')} ${r('none')} ${r('none')}`,
                bgcolor: theme.app.colors.border,
                opacity: 0.6,
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
